import React, { useState } from "react";
import styled from "styled-components";

// InnerLogic Brand Colors
const brandColors = {
  primary: {
    50: "#EBF1FD",
    100: "#D5E2F9", 
    500: "#2D70E2",
    600: "#2254AA",
  },
  secondary: {
    500: "#27CDA7",
    600: "#1D9A7D",
  },
  text: {
    dark: "#2A3039",
    gray: "#666D79",
    light: "#8B93A3",
  },
  background: {
    white: "#FFFFFF",
    lightBlue: "#F8FAFF",
  },
  neutral: {
    200: "#F2F2F2",
    500: "#DFDFDF",
  },
  status: {
    success: "#10B981",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
  }
};

const brandGradient = `linear-gradient(135deg, ${brandColors.primary["500"]} 0%, ${brandColors.secondary["500"]} 100%)`;

// Mock data for reports
const mockReports = [
  {
    id: 1,
    anchor: "Engineering",
    type: "Custom Action Plan",
    status: "generated",
    generated_date: "2024-01-20",
    member_count: 8
  },
  {
    id: 2,
    anchor: "Product",
    type: "Summary Report",
    status: "pending",
    generated_date: null,
    member_count: 5
  },
  {
    id: 3,
    anchor: "Marketing",
    type: "Trend Report",
    status: "generated",
    generated_date: "2024-01-19",
    member_count: 6
  },
  {
    id: 4,
    anchor: "Sales",
    type: "Custom Action Plan",
    status: "pending",
    generated_date: null,
    member_count: 12
  }
];

// Mock pulse templates
const pulseTemplates = [
  {
    id: 1,
    name: "Weekly Check-in",
    description: "Quick progress update and blockers",
    questions: ["How are you progressing on your action items?", "Any blockers or challenges?", "What support do you need?"]
  },
  {
    id: 2,
    name: "Leadership Assessment",
    description: "Leadership development focused questions",
    questions: ["Rate your confidence in your leadership skills", "What leadership area are you focusing on?", "How is your team responding?"]
  },
  {
    id: 3,
    name: "Goal Progress Review",
    description: "Detailed goal and milestone review",
    questions: ["What percentage complete are your goals?", "Which goals are on track?", "What would help accelerate progress?"]
  }
];

// Mock pulse history
const mockPulseHistory = [
  {
    id: 1,
    template_name: "Weekly Check-in",
    sent_date: "2024-01-18",
    responses: 8,
    total_sent: 10,
    avg_score: 7.2,
    status: "completed"
  },
  {
    id: 2,
    template_name: "Leadership Assessment", 
    sent_date: "2024-01-10",
    responses: 10,
    total_sent: 10,
    avg_score: 6.8,
    status: "completed"
  },
  {
    id: 3,
    template_name: "Goal Progress Review",
    sent_date: "2024-01-20",
    responses: 6,
    total_sent: 10,
    avg_score: null,
    status: "in_progress"
  }
];

// Styled Components
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: "Raleway", sans-serif;
  background: ${brandColors.background.white};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: ${brandColors.background.white};
  border-bottom: 1px solid ${brandColors.neutral["200"]};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: ${brandColors.text.gray};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${brandColors.neutral["200"]};
    color: ${brandColors.text.dark};
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GroupName = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0 0 4px 0;
`;

const GroupMeta = styled.div`
  font-size: 14px;
  color: ${brandColors.text.gray};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${brandColors.primary["500"]};
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${brandColors.primary["600"]};
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

// Tab Navigation
const TabContainer = styled.div`
  background: ${brandColors.background.white};
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 1px solid ${brandColors.neutral["200"]};
`;

const Tab = styled.button`
  padding: 16px 24px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.active ? brandColors.primary["500"] : 'transparent'};
  color: ${props => props.active ? brandColors.primary["500"] : brandColors.text.gray};
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;
  position: relative;

  &:hover {
    color: ${brandColors.primary["500"]};
  }
`;

const TabContent = styled.div`
  background: ${brandColors.background.white};
  min-height: 500px;
`;

// Table Components
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: ${brandColors.text.dark};
  background: ${brandColors.neutral["200"]};
  border-bottom: 1px solid ${brandColors.neutral["200"]};
  font-size: 14px;
`;

const TableRow = styled.tr`
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${props => props.clickable ? brandColors.neutral["200"] : 'transparent'};
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid ${brandColors.neutral["200"]};
  }
`;

const TableCell = styled.td`
  padding: 16px;
  color: ${brandColors.text.dark};
  vertical-align: middle;
`;

const StatusChip = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'generated': return brandColors.status.success;
      case 'approved': return brandColors.primary["500"];
      case 'sent': return brandColors.secondary["500"];
      case 'pending': return brandColors.status.warning;
      case 'on-track': return brandColors.status.success;
      case 'at-risk': return brandColors.status.warning;
      case 'off-track': return brandColors.status.error;
      case 'completed': return brandColors.status.success;
      case 'in_progress': return brandColors.status.info;
      default: return brandColors.neutral["200"];
    }
  }};
  color: white;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;
  border: none;
  
  ${props => props.primary ? `
    background: ${brandColors.primary["500"]};
    color: white;
    &:hover {
      background: ${brandColors.primary["600"]};
    }
  ` : props.success ? `
    background: ${brandColors.status.success};
    color: white;
    &:hover {
      background: ${brandColors.status.success}dd;
    }
  ` : `
         background: transparent;
     color: ${brandColors.text.gray};
     border: 1px solid ${brandColors.neutral["200"]};
     &:hover {
       background: ${brandColors.neutral["200"]};
       color: ${brandColors.text.dark};
       border-color: ${brandColors.neutral["500"]};
     }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ProgressBar = styled.div`
  width: 100px;
  height: 8px;
  background: ${brandColors.neutral["200"]};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${brandColors.status.success};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: ${brandColors.background.white};
  border-radius: 6px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid ${brandColors.neutral["200"]};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${brandColors.text.gray};
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: ${brandColors.neutral["200"]};
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

// Rich Text Editor (Simple)
const EmailEditor = styled.div`
  border: 1px solid ${brandColors.neutral["200"]};
  border-radius: 4px;
  padding: 16px;
  margin: 16px 0;
`;

const EmailSubject = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${brandColors.neutral["200"]};
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 12px;
  font-family: "Raleway", sans-serif;
  
  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

const EmailBody = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 12px;
  border: 1px solid ${brandColors.neutral["200"]};
  border-radius: 6px;
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

const Section = styled.div`
  padding: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin: 0;
`;

const PulseTemplateCard = styled.div`
  padding: 16px;
  border: 1px solid ${props => props.selected ? brandColors.primary["500"] : brandColors.neutral["200"]};
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 12px;
  background: ${props => props.selected ? brandColors.neutral["200"] : brandColors.background.white};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${brandColors.neutral["500"]};
    background: ${brandColors.neutral["200"]};
  }
`;

const TemplateTitle = styled.div`
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin-bottom: 4px;
`;

const TemplateDescription = styled.div`
  color: ${brandColors.text.gray};
  font-size: 14px;
  margin-bottom: 8px;
`;

const QuestionList = styled.div`
  color: ${brandColors.text.light};
  font-size: 12px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${brandColors.text.dark};
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${brandColors.neutral["200"]};
  border-radius: 4px;
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  
  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${brandColors.neutral["200"]};
  border-radius: 4px;
  font-size: 14px;
  font-family: "Raleway", sans-serif;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${brandColors.primary["500"]};
  }
`;

// Member Form Component
const MemberForm = ({ member, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    member_name: member?.member_name || '',
    department: member?.department || '',
    status: member?.status || 'on-track',
    progress: member?.progress || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.member_name.trim() || !formData.department.trim()) {
      alert('Please fill in all required fields');
      return;
    }
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormField>
        <FormLabel>Member Name *</FormLabel>
        <FormInput
          type="text"
          value={formData.member_name}
          onChange={(e) => handleChange('member_name', e.target.value)}
          placeholder="Enter member name"
          required
        />
      </FormField>

      <FormField>
        <FormLabel>Department *</FormLabel>
        <FormInput
          type="text"
          value={formData.department}
          onChange={(e) => handleChange('department', e.target.value)}
          placeholder="Enter department"
          required
        />
      </FormField>

      <FormField>
        <FormLabel>Status</FormLabel>
        <FormSelect
          value={formData.status}
          onChange={(e) => handleChange('status', e.target.value)}
        >
          <option value="on-track">On Track</option>
          <option value="at-risk">At Risk</option>
          <option value="off-track">Off Track</option>
        </FormSelect>
      </FormField>

      <FormField>
        <FormLabel>Progress (%)</FormLabel>
        <FormInput
          type="number"
          min="0"
          max="100"
          value={formData.progress}
          onChange={(e) => handleChange('progress', parseInt(e.target.value) || 0)}
          placeholder="0"
        />
      </FormField>

      <ModalActions>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" primary>
          {member ? 'Update Member' : 'Add Member'}
        </Button>
      </ModalActions>
    </form>
  );
};

const GroupDetailView = ({ group, plans, onBack, onPlanSelect }) => {
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState(mockReports);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: 'Your Action Plan Report is Ready',
    body: `Dear Team Member,

Your personalized action plan report has been generated and is ready for review. This report contains specific recommendations based on your recent assessments and goals.

Please review your action plan and begin implementing the recommended actions. If you have any questions or need support, don't hesitate to reach out.

Best regards,
The Leadership Team`
  });
  const [selectedReportsToSend, setSelectedReportsToSend] = useState([]);
  const [showPulseModal, setShowPulseModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pulseHistory, setPulseHistory] = useState(mockPulseHistory);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberList, setMemberList] = useState(plans);

  const handleApproveReport = (reportId) => {
    setReports(prev => prev.map(report => 
      report.id === reportId 
        ? { ...report, status: 'approved' }
        : report
    ));
  };

  const handleApproveAll = () => {
    setReports(prev => prev.map(report => 
      report.status === 'generated' 
        ? { ...report, status: 'approved' }
        : report
    ));
  };

  const handleSendReports = (reportIds) => {
    setSelectedReportsToSend(reportIds);
    setShowEmailModal(true);
  };

  const handleSendEmail = () => {
    // Simulate sending email
    setReports(prev => prev.map(report => 
      selectedReportsToSend.includes(report.id)
        ? { ...report, status: 'sent' }
        : report
    ));
    setShowEmailModal(false);
    setSelectedReportsToSend([]);
    alert(`Reports sent to ${selectedReportsToSend.length} groups successfully!`);
  };

  const handleSchedulePulse = () => {
    setShowPulseModal(true);
  };

  const handleSendPulse = () => {
    if (selectedTemplate) {
      const newPulse = {
        id: Date.now(),
        template_name: selectedTemplate.name,
        sent_date: new Date().toISOString().split('T')[0],
        responses: 0,
        total_sent: memberList.length,
        avg_score: null,
        status: "in_progress"
      };
      
      setPulseHistory(prev => [newPulse, ...prev]);
      setShowPulseModal(false);
      setSelectedTemplate(null);
      alert(`Pulse "${selectedTemplate.name}" sent to ${memberList.length} members!`);
    }
  };

  const handleAddMember = () => {
    setShowAddMemberModal(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setShowAddMemberModal(true);
  };

  const handleRemoveMember = (memberId) => {
    if (window.confirm('Are you sure you want to remove this member from the group?')) {
      setMemberList(prev => prev.filter(member => member.id !== memberId));
      alert('Member removed from group successfully!');
    }
  };

  const handleSaveMember = (memberData) => {
    if (editingMember) {
      // Update existing member
      setMemberList(prev => prev.map(member => 
        member.id === editingMember.id 
          ? { ...member, ...memberData }
          : member
      ));
      alert('Member updated successfully!');
    } else {
      // Add new member
      const newMember = {
        id: Date.now(),
        progress: 0,
        status: 'on-track',
        last_update: new Date().toISOString().split('T')[0],
        tasks_completed: 0,
        tasks_total: 0,
        ...memberData
      };
      setMemberList(prev => [...prev, newMember]);
      alert('Member added to group successfully!');
    }
    setShowAddMemberModal(false);
    setEditingMember(null);
  };

  const getDaysAgo = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const renderReportsTab = () => {
    const generatedReports = reports.filter(r => r.status === 'generated');
    const approvedReports = reports.filter(r => r.status === 'approved');
    
    // Get the report type from the first report (assuming they're all the same type)
    const reportType = reports.length > 0 ? reports[0].type : 'Custom Action Plan';
    
    return (
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>📊 Reports by Department</SectionTitle>
            <div style={{ 
              fontSize: '14px', 
              color: brandColors.text.gray, 
              marginTop: '4px' 
            }}>
              Report Type: {reportType}
            </div>
          </div>
          <ButtonGroup>
            {generatedReports.length > 0 && (
              <>
                <Button onClick={handleApproveAll}>
                  Approve All ({generatedReports.length})
                </Button>
                <Button 
                  primary 
                  onClick={() => handleSendReports(generatedReports.map(r => r.id))}
                >
                  Approve & Send All
                </Button>
              </>
            )}
            {approvedReports.length > 0 && (
              <Button 
                success 
                onClick={() => handleSendReports(approvedReports.map(r => r.id))}
              >
                Send Approved ({approvedReports.length})
              </Button>
            )}
          </ButtonGroup>
        </SectionHeader>
        
        <Table>
          <thead>
            <tr>
              <TableHeader>Department/Anchor</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Generated Date</TableHeader>
              <TableHeader>Members</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {reports.map(report => (
              <TableRow key={report.id}>
                <TableCell>
                  <strong>{report.anchor}</strong>
                </TableCell>
                <TableCell>
                  <StatusChip status={report.status}>
                    {report.status}
                  </StatusChip>
                </TableCell>
                <TableCell>
                  {report.generated_date 
                    ? new Date(report.generated_date).toLocaleDateString()
                    : 'Processing...'
                  }
                </TableCell>
                <TableCell>{report.member_count} members</TableCell>
                <TableCell>
                  <ButtonGroup>
                    {(report.status === 'generated' || report.status === 'approved') && (
                      <Button onClick={() => alert('Edit functionality coming soon!')}>
                        Edit
                      </Button>
                    )}
                    {report.status === 'generated' && (
                      <>
                        <Button onClick={() => alert('Preview functionality coming soon!')}>
                          Preview
                        </Button>
                        <Button 
                          success 
                          onClick={() => handleApproveReport(report.id)}
                        >
                          Approve
                        </Button>
                        <Button 
                          primary 
                          onClick={() => handleSendReports([report.id])}
                        >
                          Approve & Send
                        </Button>
                      </>
                    )}
                    {report.status === 'approved' && (
                      <Button 
                        success 
                        onClick={() => handleSendReports([report.id])}
                      >
                        Send to Members
                      </Button>
                    )}
                    {report.status === 'pending' && (
                      <span style={{ color: brandColors.text.gray, fontSize: '14px' }}>
                        Generating...
                      </span>
                    )}
                    {report.status === 'sent' && (
                      <StatusChip status="sent">
                        ✓ Sent
                      </StatusChip>
                    )}
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </Section>
    );
  };

  const renderGroupMembersTab = () => (
    <Section>
      <SectionHeader>
        <SectionTitle>👥 Group Members ({memberList.length})</SectionTitle>
        <Button primary onClick={handleAddMember}>
          + Add Member
        </Button>
      </SectionHeader>
      
      <Table>
        <thead>
          <tr>
            <TableHeader>Member</TableHeader>
            <TableHeader>Department</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Progress</TableHeader>
            <TableHeader>Last Update</TableHeader>
            <TableHeader>Actions</TableHeader>
          </tr>
        </thead>
        <tbody>
          {memberList.length > 0 ? (
            memberList.map(member => (
              <TableRow key={member.id}>
                <TableCell>
                  <strong>{member.member_name}</strong>
                </TableCell>
                <TableCell>{member.department}</TableCell>
                <TableCell>
                  <StatusChip status={member.status}>
                    {member.status.replace('-', ' ')}
                  </StatusChip>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ProgressBar>
                      <ProgressFill progress={member.progress} />
                    </ProgressBar>
                    <span style={{ fontSize: '12px', color: brandColors.text.gray }}>
                      {member.progress}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {new Date(member.last_update).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <ButtonGroup>
                    <Button onClick={() => onPlanSelect(member)}>
                      View Profile
                    </Button>
                    <Button onClick={() => handleEditMember(member)}>
                      Edit
                    </Button>
                    <Button onClick={() => handleRemoveMember(member.id)}>
                      Remove
                    </Button>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan="6" style={{ textAlign: 'center', color: brandColors.text.gray }}>
                No members in this group yet. Add your first member above!
              </TableCell>
            </TableRow>
          )}
        </tbody>
      </Table>
    </Section>
  );

  const renderPulsesTab = () => (
    <Section>
      <SectionHeader>
        <SectionTitle>📋 Pulse Surveys</SectionTitle>
        <ActionButton onClick={handleSchedulePulse}>
          📬 Schedule Pulse
        </ActionButton>
      </SectionHeader>
      
      <Table>
        <thead>
          <tr>
            <TableHeader>Pulse Name</TableHeader>
            <TableHeader>Sent Date</TableHeader>
            <TableHeader>Responses</TableHeader>
            <TableHeader>Avg Score</TableHeader>
            <TableHeader>Status</TableHeader>
          </tr>
        </thead>
        <tbody>
          {pulseHistory.length > 0 ? (
            pulseHistory.map(pulse => (
              <TableRow key={pulse.id}>
                <TableCell>
                  <strong>{pulse.template_name}</strong>
                </TableCell>
                <TableCell>
                  {new Date(pulse.sent_date).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {pulse.responses}/{pulse.total_sent}
                </TableCell>
                <TableCell>
                  {pulse.avg_score ? `${pulse.avg_score}/10` : '-'}
                </TableCell>
                <TableCell>
                  <StatusChip status={pulse.status}>
                    {pulse.status.replace('_', ' ')}
                  </StatusChip>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan="5" style={{ textAlign: 'center', color: brandColors.text.gray }}>
                No pulses sent yet. Schedule your first pulse above!
              </TableCell>
            </TableRow>
          )}
        </tbody>
      </Table>
    </Section>
  );

  if (!group) return null;

  return (
    <Container>
      <HeaderSection>
        <BackButton onClick={onBack}>
          <span>←</span>
          Back to Dashboard
        </BackButton>
        <HeaderTitle>
          <GroupName>{group.name}</GroupName>
          <GroupMeta>
            {group.member_count} members • Created {getDaysAgo(group.created_at)} days ago
          </GroupMeta>
        </HeaderTitle>
        <div style={{ width: '120px' }}></div>
      </HeaderSection>

      <ContentWrapper>
        <TabContainer>
          <TabNavigation>
            <Tab 
              active={activeTab === 'reports'} 
              onClick={() => setActiveTab('reports')}
            >
              📊 Reports
            </Tab>
            <Tab 
              active={activeTab === 'members'} 
              onClick={() => setActiveTab('members')}
            >
              👥 Group Members
            </Tab>
            <Tab 
              active={activeTab === 'pulses'} 
              onClick={() => setActiveTab('pulses')}
            >
              📋 Pulses
            </Tab>
          </TabNavigation>
          
          <TabContent>
            {activeTab === 'reports' && renderReportsTab()}
            {activeTab === 'members' && renderGroupMembersTab()}
            {activeTab === 'pulses' && renderPulsesTab()}
          </TabContent>
        </TabContainer>
      </ContentWrapper>

      {/* Add/Edit Member Modal */}
      {showAddMemberModal && (
        <ModalOverlay onClick={() => {setShowAddMemberModal(false); setEditingMember(null);}}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingMember ? 'Edit Member' : 'Add New Member'}</ModalTitle>
              <CloseButton onClick={() => {setShowAddMemberModal(false); setEditingMember(null);}}>×</CloseButton>
            </ModalHeader>

            <MemberForm 
              member={editingMember}
              onSave={handleSaveMember}
              onCancel={() => {setShowAddMemberModal(false); setEditingMember(null);}}
            />
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <ModalOverlay onClick={() => setShowEmailModal(false)}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Send Reports via Email</ModalTitle>
              <CloseButton onClick={() => setShowEmailModal(false)}>×</CloseButton>
            </ModalHeader>

            <div>
              <p style={{ color: brandColors.text.gray, marginBottom: '16px' }}>
                Sending reports to {selectedReportsToSend.length} group(s). Customize the email message below:
              </p>

              <EmailEditor>
                <EmailSubject
                  type="text"
                  placeholder="Email subject"
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                />
                <EmailBody
                  placeholder="Email message..."
                  value={emailData.body}
                  onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
                />
              </EmailEditor>

                             <div style={{ 
                 padding: '12px', 
                 background: brandColors.neutral["200"], 
                 borderRadius: '6px',
                 fontSize: '14px',
                 color: brandColors.text.gray
               }}>
                 💡 Reports will be attached as PDF files to this email.
               </div>
            </div>

            <ModalActions>
              <Button onClick={() => setShowEmailModal(false)}>
                Cancel
              </Button>
              <Button primary onClick={handleSendEmail}>
                Send Reports
              </Button>
            </ModalActions>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Pulse Scheduling Modal */}
      {showPulseModal && (
        <ModalOverlay onClick={() => setShowPulseModal(false)}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Schedule Pulse Survey</ModalTitle>
              <CloseButton onClick={() => setShowPulseModal(false)}>×</CloseButton>
            </ModalHeader>

            <div>
              <p style={{ color: brandColors.text.gray, marginBottom: '20px' }}>
                Select a pulse template to send to all {memberList.length} members in this group.
              </p>

              {pulseTemplates.map(template => (
                <PulseTemplateCard
                  key={template.id}
                  selected={selectedTemplate?.id === template.id}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <TemplateTitle>{template.name}</TemplateTitle>
                  <TemplateDescription>{template.description}</TemplateDescription>
                  <QuestionList>
                    {template.questions.length} questions • {template.questions.join(' • ')}
                  </QuestionList>
                </PulseTemplateCard>
              ))}
            </div>

            <ModalActions>
              <Button onClick={() => setShowPulseModal(false)}>
                Cancel
              </Button>
              <Button 
                primary 
                disabled={!selectedTemplate}
                onClick={handleSendPulse}
              >
                Send Pulse to {memberList.length} Members
              </Button>
            </ModalActions>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default GroupDetailView; 